import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useRef } from 'react';
import { useModal } from 'flow/contexts/ModalProvider';
import { getFileExtensionWithDot, MAX_ATTACHMENT_SIZE_DEFAULT } from 'flow/components/tickets/Input/InputActions';
import { FILE_SIZE_EXCEEDED, FILE_TYPE_NOT_ALLOWED } from 'flow/components/tickets/Input/Attachments/AttachmentUploadError';
import { replyChannelSupportsAttachments } from 'flow/components/tickets/Input/hooks/useExternalReplyChannel';
var SIGNATURE_ALL_FILE_TYPES = '*/*';
var isFileTypeAllowed = function isFileTypeAllowed(file, allowedAttachmentsData, blockedMimeTypes) {
  return !(blockedMimeTypes !== null && blockedMimeTypes !== void 0 && blockedMimeTypes.includes(file.type)) && !(blockedMimeTypes !== null && blockedMimeTypes !== void 0 && blockedMimeTypes.includes(SIGNATURE_ALL_FILE_TYPES)) && (!allowedAttachmentsData || allowedAttachmentsData.has(file.type) || allowedAttachmentsData.has(SIGNATURE_ALL_FILE_TYPES));
}; //About the check for !allowedAttachmentsData: If no such data is present, if we for some reason have not received the restriction data from the backend, we let the user send all file types; backend will validate anyway.

var useAttachmentHandler = function useAttachmentHandler(_ref) {
  var setMessageContentInternal = _ref.setMessageContentInternal,
    setMessageContentReplyExternal = _ref.setMessageContentReplyExternal,
    tabSelected = _ref.tabSelected,
    allowInputInExternalReply = _ref.allowInputInExternalReply,
    externalChannel = _ref.externalChannel,
    userAllowedToSendAttachments = _ref.userAllowedToSendAttachments,
    allowedAttachmentsData = _ref.allowedAttachmentsData,
    blockedMimeTypes = _ref.blockedMimeTypes;
  var fileInputRef = useRef(null);
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var handleDrop = function handleDrop(event) {
    if (!userAllowedToSendAttachments) return;
    if (tabSelected === 'reply_external' && (!allowInputInExternalReply || !replyChannelSupportsAttachments(externalChannel))) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    var file = event.dataTransfer.files[0];
    if (file) {
      handleAddAttachment(file);
    }
  };
  var handleAddAttachment = function handleAddAttachment(file) {
    if (!userAllowedToSendAttachments) return;
    if (!file) return;
    if (tabSelected === 'reply_external') {
      if (!isFileTypeAllowed(file, allowedAttachmentsData, blockedMimeTypes)) {
        //At the time of writing we don't have any data for allowed attachments for internal notes,
        //hence checking only for external replies.
        openModal('ticketAttachmentNotAllowed', {
          channel: externalChannel,
          fileExtensionUserFriendly: getFileExtensionWithDot(file.name),
          fileMimeType: file.type,
          reason: FILE_TYPE_NOT_ALLOWED
        });
        return;
      }
    }
    var maxFileSize = (allowedAttachmentsData === null || allowedAttachmentsData === void 0 ? void 0 : allowedAttachmentsData.getIn([file.type, 'maxSizeBytes'])) || (allowedAttachmentsData === null || allowedAttachmentsData === void 0 ? void 0 : allowedAttachmentsData.getIn([SIGNATURE_ALL_FILE_TYPES, 'maxSizeBytes'])) || MAX_ATTACHMENT_SIZE_DEFAULT; // Default to 20 MB if not specified

    if (file.size > maxFileSize) {
      openModal('ticketAttachmentNotAllowed', {
        channel: tabSelected === 'internal' ? 'internal' : externalChannel,
        fileExtensionUserFriendly: getFileExtensionWithDot(file.name),
        fileMimeType: file.type,
        reason: FILE_SIZE_EXCEEDED
      });
      return;
    }
    var attachmentObject = {
      name: file.name,
      file: file
    };
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function () {
      var _file$type, _reader$result;
      var imageSrc = (file === null || file === void 0 || (_file$type = file.type) === null || _file$type === void 0 ? void 0 : _file$type.startsWith('image')) && ((_reader$result = reader.result) === null || _reader$result === void 0 ? void 0 : _reader$result.toString()) || null;
      if (tabSelected === 'reply_external') {
        setMessageContentReplyExternal(function (prev) {
          return _objectSpread(_objectSpread({}, prev), {}, {
            attachment: attachmentObject,
            imageSrc: imageSrc
          });
        });
      } else {
        setMessageContentInternal(function (prev) {
          return _objectSpread(_objectSpread({}, prev), {}, {
            attachment: attachmentObject,
            imageSrc: imageSrc
          });
        });
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
  };
  var handleRemoveAttachment = function handleRemoveAttachment() {
    if (tabSelected === 'reply_external') {
      setMessageContentReplyExternal(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          attachment: null,
          imageSrc: null
        });
      });
    } else {
      setMessageContentInternal(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          attachment: null,
          imageSrc: null
        });
      });
    }
  };
  var handlePasteImage = function handlePasteImage(e) {
    if (!userAllowedToSendAttachments) return;
    if (tabSelected === 'reply_external' && (!allowInputInExternalReply || !replyChannelSupportsAttachments(externalChannel))) {
      return;
    }
    var items = e.clipboardData.items;
    var file = null;
    for (var _i = 0, _Object$values = Object.values(items); _i < _Object$values.length; _i++) {
      var item = _Object$values[_i];
      if (item.type.startsWith('image')) {
        file = item.getAsFile();
      }
    }
    if (file) {
      handleAddAttachment(file);
      e.preventDefault();
    }
  };
  var onAttachmentAspectRatioFound = useCallback(function (aspectRatio, tabSelected) {
    var setMessageContent = tabSelected === 'internal' ? setMessageContentInternal : setMessageContentReplyExternal;
    setMessageContent(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        attachment: _objectSpread(_objectSpread({}, prev.attachment), {}, {
          aspectRatio: aspectRatio
        })
      });
    });
  }, [setMessageContentInternal, setMessageContentReplyExternal]);
  return {
    fileInputRef: fileInputRef,
    handleDrop: handleDrop,
    handleAddAttachment: handleAddAttachment,
    handleRemoveAttachment: handleRemoveAttachment,
    handlePasteImage: handlePasteImage,
    onAttachmentAspectRatioFound: onAttachmentAspectRatioFound
  };
};
export default useAttachmentHandler;